import { UserType } from "@/types/IdentityTypes";
import type { LoginScreen } from "@/services/AuthService";

/**
 * Reads the screen intent (`screen=signin|signup`) packed into the `login_hint`
 * of an OIDC return URL. IdentityServer's LoginUrl is our own /signin page, so
 * this lets that page redirect on to /signup while keeping the same authorize
 * round-trip. Accepts relative return URLs (IdentityServer emits them) via the
 * current origin as a parsing base.
 */
export function extractScreenHintFromReturnUrl(
  returnUrl: string,
): LoginScreen | null {
  try {
    if (!returnUrl) {
      return null;
    }

    const loginHint = new URL(
      returnUrl,
      window.location.origin,
    ).searchParams.get("login_hint");

    const screen = loginHint?.match(/screen=(\w+)/)?.[1];

    return screen === "signin" || screen === "signup" ? screen : null;
  } catch (error) {
    console.error("Error parsing return URL for screen hint:", error);
    return null;
  }
}

export function extractUserTypeFromReturnUrl(
  returnUrl: string,
): UserType | null {
  try {
    if (!returnUrl) {
      return null;
    }

    const url = new URL(returnUrl);
    const loginHint = url.searchParams.get("login_hint");

    if (loginHint) {
      const match = loginHint.match(/userType=(\w+)/);
      const userTypeString = match?.[1] ?? null;

      if (userTypeString) {
        if (Object.values(UserType).includes(userTypeString as UserType)) {
          return userTypeString as UserType;
        } else {
          console.error(
            `Invalid user type from login hint: ${userTypeString}. Valid types are:`,
            Object.values(UserType),
          );
          return null;
        }
      }
    }

    return null;
  } catch (error) {
    console.error("Error parsing return URL for user type:", error);
    return null;
  }
}
